import { BodyR } from '@pensionbee/honeypot-ui';
import * as RadixSwitch from '@radix-ui/react-switch';
import { twMerge } from 'tailwind-merge';

type SwitchProps = {
  /** Whether the switch is checked */
  checked: boolean;
  /** Handler for switch state change */
  onCheckedChange: (checked: boolean) => void;
  /** Prevents user interaction */
  disabled?: boolean;
  /** Error message for validation feedback */
  error?: string;
  /** Accessible label for screen readers */
  ariaLabel?: string;
  /** Label displayed next to the switch */
  label?: string;
  /** Custom className for wrapper */
  className?: string;
  /** Configurable label position */
  labelPosition?: 'left' | 'right';
};

export const Switch = ({
  checked,
  onCheckedChange,
  disabled,
  error,
  ariaLabel,
  label,
  className,
  labelPosition = 'right',
}: SwitchProps) => (
  <label
    className={twMerge(
      'flex items-center',
      labelPosition === 'left'
        ? 'w-full justify-between gap-12'
        : 'flex-row gap-8',
      className,
    )}
    data-rc="Switch"
  >
    {labelPosition === 'left' && label && <BodyR>{label}</BodyR>}
    <RadixSwitch.Root
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={ariaLabel ?? label}
      aria-disabled={disabled}
      data-error={error}
      className={twMerge(
        'relative h-28 w-48 shrink-0 cursor-pointer rounded-full bg-grey-3 transition-colors duration-500',
        'data-[disabled]:cursor-not-allowed data-[state=checked]:bg-blue-100 data-[disabled]:opacity-50',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-100 focus-visible:ring-2 focus-visible:ring-blue-100/50',
        error
          ? 'outline outline-2 outline-red-100'
          : 'focus:outline focus:outline-2 focus:outline-blue-100',
      )}
    >
      <RadixSwitch.Thumb className="block size-24 translate-x-2 rounded-full bg-background transition-all duration-500 data-[state=checked]:translate-x-[22px]" />
    </RadixSwitch.Root>
    {labelPosition === 'right' && label && <BodyR>{label}</BodyR>}
  </label>
);
